import { useState, useEffect, useRef } from 'react';


const WS_HEARTBEAT_INTERVAL = 1 * 60 * 1000; // 1 minute in milliseconds
// const WS_HEARTBEAT_INTERVAL = 10 * 1000;

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

// const getCloseCodeMeaning = (code: number): string => {
//   const closeCodes: Record<number, string> = {
//     1000: "Normal closure",
//     1001: "Going away (page close)",
//     1002: "Protocol error",
//     1003: "Unsupported data",
//     1005: "No status received",
//     1006: "Abnormal closure (connection lost)",
//     1007: "Invalid frame payload data",
//     1008: "Policy violation",
//     1009: "Message too big",
//     1010: "Missing extension (client)",
//     1011: "Internal error (server)",
//     1012: "Service restart",
//     1013: "Try again later",
//     1014: "Bad gateway",
//     1015: "TLS handshake failure"
//   };

//   return closeCodes[code] || `Unknown close code (${code})`;
// };

type SignalingMessage = {
  action: 'message',
  type: 'offer' | 'answer' | 'candidate';
  senderId: string;
  targetId: string;
  payload: any;
};

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'failed';

export const useWebRTC = (myId: string | undefined) => {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [rtt, setRtt] = useState<number | null>(null);

  const [lowestRtt, setLowestRtt] = useState<number | null>(null);

  const [connectionDetails, setConnectionDetails] = useState<{
    local: { ip: string; port: number; type: string };
    remote: { ip: string; port: number; type: string };
    rtt: number | null;
  } | null>(null);
  // const [peerId, setPeerId] = useState<string | null>(null);
  // const [isSignalingReady, setIsSignalingReady] = useState(false);
  const peerIdRef = useRef<string | null>(null);

  const isSignalingReadyRef = useRef(false);
  const socketRef = useRef<WebSocket | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const pingTimeRef = useRef<number | null>(null);
  const pendingMessages = useRef<any[]>([]); // Queue for messages

  const cleanup = () => {
    dcRef.current?.close();
    pcRef.current?.close();
    dcRef.current = null;
    pcRef.current = null;
    setStatus('disconnected');
    peerIdRef.current = null;
    setRtt(null);
    pingTimeRef.current = null;
  };

  // Utility to send or queue messages via websocket
  const sendMessage = (message: SignalingMessage) => {
    if (isSignalingReadyRef.current && socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
      console.log('Sent message:', message);
    } else {
      pendingMessages.current.push(message);
      console.log('Queued message:', message);
    }
  };

  const handleMessage = (msg: SignalingMessage) => {
    console.log('handling message', { msg });

    if (!myId || msg.targetId !== myId) {
      console.warn("got a message that wasn't for me, no-op", { msg })
      return;
    }

    const pc = pcRef.current;

    if (msg.type === 'offer') {
      console.log('Processing offer from:', msg.senderId);
      if (pc) cleanup();
      const newPc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
      pcRef.current = newPc;
      peerIdRef.current = msg.senderId;

      newPc.onicecandidate = (e) => {
        console.log('got a candidate', { candidate: e.candidate });

        if (e.candidate) {
          sendMessage({
            action: 'message',
            type: 'candidate',
            senderId: myId,
            targetId: msg.senderId,
            payload: e.candidate,
          });
        }
      };

      newPc.ondatachannel = (e) => {
        dcRef.current = e.channel;
        setupDataChannel(e.channel);
      };

      newPc.setRemoteDescription(new RTCSessionDescription(msg.payload))
        .then(() => { console.log("remote description set successfully."); return newPc.createAnswer() })
        .then(answer => newPc.setLocalDescription(answer))
        .then(() => {
          console.log("local description set successfully.");
          sendMessage({
            action: 'message',
            type: 'answer',
            senderId: myId,
            targetId: msg.senderId,
            payload: newPc.localDescription,
          });
        })
        .catch(err => {
          setStatus('failed');
          cleanup();
        });
    } else if (msg.type === 'answer' && pc && peerIdRef.current === msg.senderId) {

      pc.setRemoteDescription(new RTCSessionDescription(msg.payload))
        .catch(err => {
          console.error('Error setting answer:', err);
          setStatus('failed');
          cleanup();
        });
    } else if (msg.type === 'candidate' && pc && peerIdRef.current === msg.senderId) {
      console.log('Received ICE candidate:', msg.payload);
      pc.addIceCandidate(new RTCIceCandidate(msg.payload))
        .catch(err => console.error('Error adding candidate:', err));
    }
  };

  const setupDataChannel = (dc: RTCDataChannel) => {
    dc.onopen = () => {
      console.log('Data channel opened');

      setStatus('connected');
      // Log selected candidate pair
      const logConnectionDetails = async () => {
        if (!pcRef.current) return;
        try {
          const stats = await pcRef.current.getStats();
          let selectedPair: any = null;
          let localCandidate: any = null;
          let remoteCandidate: any = null;

          stats.forEach(stat => {
            if (stat.type === 'candidate-pair' && stat.state === 'succeeded' && stat.nominated) {
              selectedPair = stat;
            }
            if (stat.type === 'local-candidate' && selectedPair?.localCandidateId === stat.id) {
              localCandidate = stat;
            }
            if (stat.type === 'remote-candidate' && selectedPair?.remoteCandidateId === stat.id) {
              remoteCandidate = stat;
            }
          });

          if (selectedPair && localCandidate && remoteCandidate) {
            const details = {
              local: {
                ip: localCandidate.address,
                port: localCandidate.port,
                type: localCandidate.candidateType,
              },
              remote: {
                ip: remoteCandidate.address,
                port: remoteCandidate.port,
                type: remoteCandidate.candidateType,
              },
              rtt: selectedPair.currentRoundTripTime ? selectedPair.currentRoundTripTime * 1000 : rtt,
            };
            setConnectionDetails(details);
            console.log('WebRTC Connection:', details);
          }
        } catch (error) {
          console.error('Error logging connection details:', error);
        }
      };
      logConnectionDetails(); // Log immediately
      const logInterval = setInterval(logConnectionDetails, 5000); // Log every 5s

      const pingInterval = setInterval(() => {
        if (dc.readyState === 'open') {
          pingTimeRef.current = performance.now();
          dc.send(JSON.stringify({ type: 'ping', time: pingTimeRef.current }));
        }
      }, 500);
      dc.onclose = () => {
        clearInterval(pingInterval as any);
        clearInterval(logInterval as any);
        setConnectionDetails(null);
        cleanup();
      };
    };
    dc.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'ping') {
        dc.send(JSON.stringify({ type: 'pong', time: data.time }));
      } else if (data.type === 'pong' && pingTimeRef.current) {
        const currentRtt = performance.now() - pingTimeRef.current;
        setRtt(currentRtt);
        setLowestRtt(prev => (prev === null || currentRtt < prev ? currentRtt : prev));
        pingTimeRef.current = null;
      }
    };
    dc.onerror = (error) => {
      console.log('Data channel error:', error);
    };
  };

  // connect to websocket immediately
  useEffect(() => {
    if (!myId) return;

    const ws = new WebSocket(`${process.env.REACT_APP_WEBSOCKET_URL}?beaconId=${myId}`);
    socketRef.current = ws;

    ws.onopen = () => {
      console.log('websocket opened');
      isSignalingReadyRef.current = true;

      pendingMessages.current.forEach(msg => {
        ws.send(JSON.stringify(msg))
      });
      pendingMessages.current = [];
    };
    ws.onmessage = (e) => {
      console.log("Websocket received message:", e.data)
      handleMessage(JSON.parse(e.data));
    }

    // Start sending heartbeats every 30 minutes
    const sendHeartbeat = () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          action: 'heartbeat',
          beaconId: myId
        }));
        console.log('Sent heartbeat for beaconId:', myId);
      } else {
      }
    };

    const intervalId = setInterval(sendHeartbeat, WS_HEARTBEAT_INTERVAL);

    ws.onclose = (e) => {
      clearInterval(intervalId as any);
      console.log('WebSocket closed', { readyState: ws.readyState, e });
      cleanup();
    };

    ws.onerror = (error) => {
      console.log('WebSocket error:', error);
      setStatus('failed');
    };

    return () => {
      // ws.close();
      // Close the WebSocket
      clearInterval(intervalId as any);
      if (socketRef.current) {
        console.log('Closing WebSocket connection');
        socketRef.current.close();
        socketRef.current = null;
      }
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId]);

  const connect = (targetId: string) => {
    if (!myId || status !== 'disconnected') return;

    console.log('webRTC: connecting to peer', { myId, targetId });
    setStatus('connecting');
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pcRef.current = pc;

    // setPeerId(targetId);
    peerIdRef.current = targetId

    pc.oniceconnectionstatechange = () => console.log('ICE state:', pc.iceConnectionState);
    pc.onsignalingstatechange = () => console.log('Signaling state:', pc.signalingState);
    pc.onicegatheringstatechange = () => console.log('ICE gathering state:', pc.iceGatheringState);
    pc.onconnectionstatechange = () => console.log('Connection state:', pc.connectionState);
    pc.onicecandidateerror = (event) => {
      console.log('ICE candidate error:', event);
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        console.log('ICE candidate generated', e.candidate);

        sendMessage({
          action: 'message',
          type: 'candidate',
          senderId: myId,
          targetId,
          payload: e.candidate,
        });
      }
    };

    const dc = pc.createDataChannel('ping');
    dcRef.current = dc;
    setupDataChannel(dc);

    console.log('webRTC: data channel created', { dc });


    pc.createOffer()
      .then(offer => {
        console.log('webRTC: offer created', { offer });

        return pc.setLocalDescription(offer)
      })
      .then(() => {
        console.log('Local description set, sending offer');
        sendMessage({
          action: 'message',
          type: 'offer',
          senderId: myId,
          targetId,
          payload: pc.localDescription,
        });
      })
      .catch(err => {
        console.error('Error creating offer:', err);
        setStatus('failed');
        cleanup();
      });
  };

  const disconnect = () => {
    // Reset signaling ready flag
    isSignalingReadyRef.current = false;
    // Clean up WebRTC
    cleanup();
  };

  const sendPing = () => {
    if (dcRef.current?.readyState === 'open') {
      pingTimeRef.current = performance.now();
      dcRef.current.send(JSON.stringify({ type: 'ping', time: pingTimeRef.current }));
    }
  };

  return { connect, disconnect, sendPing, status, rtt, lowestRtt, peerId: peerIdRef.current, connectionDetails };
};